import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import InfoCard from "./InfoCard";
import Lottie from "lottie-react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import "./LandingPage.css";
import Footer from "./Footer";

const heroSlides = [
    {
        src: `${process.env.PUBLIC_URL}/marketing/at-the-market.jpg`,
        text: ""
    },
    {
        src: `${process.env.PUBLIC_URL}/marketing/paris-screen-shot-phone-template.jpg`,
        text: "Master Essential Vocabulary in Weeks, Not Months."
    },
    {
        src: `${process.env.PUBLIC_URL}/marketing/golden-gate-bridge-screen-shot-phone-template.jpg`,
        text: "Learn Anywhere, Anytime. Your Personal Language Tutor."
    }
];

function LandingPage({ closeMenu }) {
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [typeNLearnAnim, setTypeNLearnAnim] = useState(null);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setCurrentSlideIndex((prevIndex) => (prevIndex + 1) % heroSlides.length);
        }, 4000); // Change slide every 4 seconds

        // Fetch Lottie animations
        fetch(`${process.env.PUBLIC_URL}/marketing/onboarding4.json`)
            .then(res => res.json())
            .then(data => setTypeNLearnAnim(data))
            .catch(err => console.error("Error loading onboarding4 animation:", err));

        return () => clearInterval(intervalId);
    }, []);

    const cardData = [
        {
            title: "Mastery Through vocabulary",
            content: [
                `Mastering a basic vocabulary is fundamental to learning a new language. Language learning programs can do an excellent job of teaching you the syntax of a language, but often dump numerous new words on you in each lesson. This can feel overwhelming and discouraging.`,
                `There isn't a one-size-fits-all rule, but linguistic research provides some general guidelines on vocabulary size for effective communication in a new language.`,
                `Studies suggest that learning the most frequently used 2,000 to 3,000 word families can enable you to handle everyday conversations and understand a significant portion of written texts. For example, in English, the 2,000 most common words account for about 80-90% of the words used in daily communication.`,
                `The goal of Click-n-Learn is to make learning a basic vocabulary easy, fast and enjoyable. There are two different activities: <b>Pick-n-Learn</b> where you select the correct answer from a list of possible answers, and <b>Type-n-Learn</b> where you hone your learning by typing the correct answer, made easier with available hints. Essential functions are controlled through an easy to use settings screen enabling you to adapt Click-n-Learn to your unique learning style.`,
            ],
        },
        {
            title: "Perfect Your Pronunciation",
            content: [
                `Don't just read it—hear it! Click-n-Learn features integrated Text-to-Speech (TTS) audio, allowing you to hear the correct pronunciation of every word you learn.`,
                `Listening is key to mastering a new language. With our clear, accurate audio, you can train your ear to recognize sounds and intonations, ensuring you develop a natural accent.`,
                `By combining visual learning with auditory reinforcement, you'll memorize words faster and build the confidence to speak out loud.`
            ],
        },
        {
            title: 'Three Interactive Modes',
            content: [
                `At Click-n-Learn, we understand that different learning methods can significantly impact how effectively you absorb new vocabulary. That's why we've developed three distinct activities to cater to your unique learning needs: Pick-n-Learn, Type-n-Learn, and Slide-n-Learn.`,
                `<b>Pick-n-Learn: Strengthen Recognition through Multiple Choice</b>`,
                `In the Pick-n-Learn activity, you enhance your vocabulary by selecting the correct answer from a list of possible options. This method leverages recognition memory, which is the ability to identify previously encountered words or concepts. Recognition tasks are particularly effective for beginners as they provide contextual cues that make learning less daunting. By repeatedly recognizing the correct words in various contexts, you build a strong foundation that helps in quickly expanding your vocabulary. This approach reduces cognitive load, making it easier to remember new words without feeling overwhelmed.`,
                `<b>Type-n-Learn: Boost Recall with Active Engagement</b>`,
                `The Type-n-Learn activity takes your learning a step further by encouraging you to type the correct answer, with hints available to guide you if needed. This method engages recall memory, which requires you to retrieve information without explicit prompts. Actively recalling and typing out words reinforces neural pathways associated with language production. This not only improves your spelling and writing skills but also enhances your ability to use the new vocabulary spontaneously in conversations. Research shows that active recall practices significantly improve memory retention compared to passive study methods.`,
                `<b>Slide-n-Learn: Master Sentence Structure through Phrase Building</b>`,
                `The Slide-n-Learn activity connects individual words into meaningful phrases by having you drag and drop words into the correct order. This hands-on approach builds your understanding of sentence structure and syntax in your target language. Manipulating language visually bridges the gap between learning individual vocabulary words and achieving natural conversational flow, helping you instinctively grasp how words actually function together in context.`,
                `<b>Why a Multi-Method Approach Enhances Your Learning Experience</b>`,
                `By incorporating recognition, recall, and structural building exercises, Click-n-Learn offers a comprehensive learning experience that aligns with established cognitive learning theories. Recognition activities are excellent for initial exposure. Recall activities solidify that knowledge for fluency. Finally, structural exercises like Slide-n-Learn train your brain on the natural rhythm and syntax of the language.`,
                `This multi-method approach ensures that you're not just memorizing words but truly integrating them into your active vocabulary. It caters to different learning styles and allows you to progress at your own pace, making the journey of learning a new language more personalized and effective.`
            ],
        },
        {
            title: "Not Just For Languages!",
            content: [
                `Learn any subject that could otherwise be learned in a flash card format. Create your own sets with a simple text format.`,

                `<b>Countries and Their Capitals:</b>\n` +
                `Canada, Ottawa\n` +
                `Brazil, Brasília\n\n`,

                `<b>Chemical Elements and Their Symbols:</b>\n` +
                `Oxygen, O\n` +
                `Gold, Au\n\n`,

                `<b>Historical Events and Dates:</b>\n` +
                `Moon Landing, 1969\n` +
                `Fall of the Berlin Wall, 1989\n\n`,

                `<b>Organic Compounds and Their Functional Groups:</b>\n` +
                `Ethanol, Alcohol (-OH) group\n` +
                `Acetone, Ketone (>C=O) group\n\n` +

                `<b>Common Names and IUPAC Names of Organic Compounds:</b>\n` +
                `Acetic Acid, Ethanoic Acid\n` +
                `Formaldehyde, Methanal\n\n`

            ],
        },
    ];

    return (
        <div className="landing-container">
            {/* Hero Carousel Section */}
            <div className="hero-carousel-container">
                {heroSlides.map((slide, index) => (
                    <div
                        key={index}
                        className={`hero-slide ${index === currentSlideIndex ? 'active' : ''}`}
                        style={{ backgroundImage: `url(${slide.src})` }}
                    >
                        {slide.text && (
                            <div className="hero-slide-text-overlay">
                                <h1>{slide.text}</h1>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            {/* Activities Section */}
            <div className="activities">
                <h2 style={{ color: "#1573bb", marginBottom: "1rem" }}>
                    Supports English, French, German, Indonesian, Italian, Japanese, Malay,
                    Mandarin, Spanish and Thai
                </h2>
                <h1>Three Ways to Learn...</h1>
                <div className="activity-row">
                    <div className="activity-text">
                        <h3>Pick-N-Learn</h3>
                        <p>
                            Just touch the correct answer for each question, or
                            alternate Questions and Answers for real power
                            learning.
                        </p>
                    </div>
                    <div className="activity-image">
                        <div className="lottie-wrapper">
                            <DotLottieReact src={`${process.env.PUBLIC_URL}/marketing/onboarding3.lottie`} loop={true} autoplay={true} style={{ width: '100%', height: '100%' }} />
                        </div>
                    </div>
                </div>
                <div className="activity-row">
                    <div className="activity-text">
                        <h3>Type-N-Learn</h3>
                        <p>
                            Fill in the blanks style with hints and automatic
                            answer checking.
                        </p>
                    </div>
                    <div className="activity-image">
                        {typeNLearnAnim ? (
                            <div className="lottie-wrapper">
                                <Lottie animationData={typeNLearnAnim} loop={true} autoplay={true} />
                            </div>
                        ) : (
                            <img
                                src={`${process.env.PUBLIC_URL}/marketing/type-n-learn-indo.jpg`}
                                alt="Type-n-Learn"
                            />
                        )}
                    </div>
                </div>
                <div className="activity-row">
                    <div className="activity-text">
                        <h3>Slide-N-Learn</h3>
                        <p>
                            Drag words down to the answer box to correctly form the phrase in your learning language.
                        </p>
                    </div>
                    <div className="activity-image">
                        <div className="lottie-wrapper">
                            <DotLottieReact src={`${process.env.PUBLIC_URL}/marketing/onboarding5.lottie`} loop={true} autoplay={true} style={{ width: '100%', height: '100%' }} />
                        </div>
                    </div>
                </div>
                <div className="activity-row">
                    <div className="activity-text">
                        <h3>Download Files</h3>
                        <p>
                            Choose from hundreds of free language learning
                            files. Or create your own files with a simple text
                            format:
                        </p>
                        <code className="custom-code">
                            question, answer <br />
                            Terima Kasih, Thank you <br />
                            Sama-sama, You're welcome
                        </code>
                    </div>
                    <div className="activity-image">
                        <img
                            src={`${process.env.PUBLIC_URL}/marketing/load-words.jpg`}
                            alt="Download Files"
                        />
                    </div>
                </div>
                <div className="activity-row">
                    <div className="activity-text">
                        <h3>Customize Settings</h3>
                        <p>
                            Customize and Personalize your learning experience
                            with easy to use settings.
                        </p>
                    </div>
                    <div className="activity-image">
                        <img
                            src={`${process.env.PUBLIC_URL}/marketing/settings-screen.jpg`}
                            alt="Settings Screen"
                        />
                    </div>
                </div>
                {/* Embedded demo video below Customize Settings */}
                <div className="activity-row video-row" aria-label="Demo video">
                    <div className="activity-text">
                        <h3>Quick Demo</h3>
                        <p>Watch a short video demonstration. See how easy Click-n-Learn is to use and how fast you'll learn a new langague!</p>
                    </div>
                    <div className="activity-image">
                        <div className="video-wrapper">
                            <iframe
                                src="https://www.youtube.com/embed/E_DD8LjTqiM?rel=0&modestbranding=1&showinfo=0&playsinline=1&iv_load_policy=3&controls=1"
                                title="Click-n-Learn demo"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                        </div>
                    </div>
                </div>
            </div>

            {/* New Cards Section */}
            <div className="additional-info">
                <h1>Learn More</h1>
                <div className="cards-container">
                    {cardData.map((card, index) => (
                        <InfoCard
                            key={index}
                            title={card.title}
                            content={card.content}
                        />
                    ))}
                </div>
            </div>

            <div className="floating-badges-container">
                <a href="https://play.google.com/store/apps/details?id=com.legacyatsea.clicknlearn" target="_blank" rel="noopener noreferrer">
                    <img src={`${process.env.PUBLIC_URL}/GetItOnGooglePlay_Badge_Web_color_English.png`} alt="Get it on Google Play" className="floating-store-badge" />
                </a>
                <Link to="/get-click-n-learn" className="ios-badge-wrapper" onClick={closeMenu}>
                    <img src={`${process.env.PUBLIC_URL}/app-store-badge.svg`} alt="Download on the App Store" className="floating-store-badge" />
                    <span className="badge-coming-soon">Coming Soon</span>
                </Link>
            </div>
            <Footer />
        </div>
    );
}

export default LandingPage;
